import type {ReactElement} from 'react';
import React from 'react';
import {useIsVideoOrHasMultiPages} from '@Hooks/poster-editor/useIsVideoOrHasMultiPages';
import styles from './poster-editor-web-bottom-bar-button.module.scss';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {getPosterDuration, updateIsTimelineModeActive} from '@Components/poster-editor/poster-editor-reducer';
import {convertSecondsToMMSSFormat} from '@Utils/date.util';
import {usePosterHasAudio} from '@Hooks/poster-editor/usePosterHasAudio';
import {IconPlayPause} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/icon-play-pause/icon-play-pause';
import {onPosterPause, onPosterPlay} from '@Components/poster-editor/library/poster-editor-library';
import {AnimatePresence, motion} from 'framer-motion';

export function PosterEditorWebBottomBarButton(): ReactElement | null {
  const isVideoOrHasMultiPages = useIsVideoOrHasMultiPages();
  const doesPosterHaveAudio = usePosterHasAudio();
  const posterDuration = useAppSelector(getPosterDuration);
  const currentTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });
  const isPlaying = useAppSelector((state) => {
    return state.posterEditor.isPlaying;
  });
  const dispatch = useAppDispatch();

  if (!isVideoOrHasMultiPages || !posterDuration) {
    return null;
  }

  const isPlayOrWasPlaying = (): boolean => {
    return isPlaying || currentTime < posterDuration;
  };

  const getTextContent = (): ReactElement => {
    const fadeVariants = {
      initial: {opacity: 0},
      animate: {opacity: 1},
      exit: {opacity: 0},
    };
    if (isPlayOrWasPlaying()) {
      return (
        <motion.div key="playing-text" variants={fadeVariants} initial="initial" animate="animate" exit="exit">
          <Text className={styles.durationText} val={`${convertSecondsToMMSSFormat(currentTime)} / ${convertSecondsToMMSSFormat(posterDuration)}`} size={TextSize.XXSMALL} bold />
        </motion.div>
      );
    }

    return (
      <motion.div key="paused-text" variants={fadeVariants} initial="initial" animate="animate" exit="exit">
        <Text className={`${isPlaying ? 'content-secondary' : ''} ${styles.durationText}`} val={convertSecondsToMMSSFormat(posterDuration)} size={TextSize.XXSMALL} bold />
      </motion.div>
    );
  };

  const onClickDurationContainer = (): void => {
    if (isPlaying) {
      onPosterPause();
    } else {
      onPosterPlay();
    }
  };

  const getDurationForPoster = (): ReactElement => {
    return (
      <div
        onClick={onClickDurationContainer}
        className={`spacing-p-l-2 spacing-p-r-2 flex-h-row flex-items-center radius-4 _cursor-pointer hover-transition-all ${styles.durationContainer} ${isPlayOrWasPlaying() ? styles.bigWidth : ''}`}
      >
        <IconPlayPause className={styles.playPauseIcon} size={IconSize.SIZE_ICON_16} shape={IconShape.NONE} />
        <AnimatePresence mode="wait">{getTextContent()}</AnimatePresence>
      </div>
    );
  };
  const getEditTimelineButton = (): ReactElement => {
    return (
      <div onClick={turnOnTimelineMode} className={`flex-center radius-8 _cursor-pointer spacing-p-2 ${styles.timelineButton}`}>
        <Icon icon="icon-pencil" size={IconSize.SIZE_ICON_16} />
        <Text val={window.i18next.t('pmwjs_edit_timeline')} size={TextSize.XXSMALL} bold className={styles.timelineText} />
      </div>
    );
  };

  const turnOnTimelineMode = (): void => {
    dispatch(updateIsTimelineModeActive(true));
  };

  const onClickAudioButton = (): void => {
    turnOnTimelineMode();
  };

  return (
    <div className={`${styles.wrapper} flex flex-h-row`}>
      <div className={`${styles.container} spacing-m-r-2 shadow-static-elevation-1 flex-h-row flex-items-center _fit-width radius-8 spacing-p-1`}>
        {getDurationForPoster()}
        <div className={`${styles.divider} spacing-m-r-2 spacing-m-l-2`} />
        {getEditTimelineButton()}
      </div>
      {doesPosterHaveAudio ? (
        <Icon onClick={onClickAudioButton} className={styles.audioIconContainer} icon="icon-audio" size={IconSize.SIZE_ICON_20} type={IconType.SHADOW} shape={IconShape.SQUARE} />
      ) : null}
    </div>
  );
}
